import React, { useState } from 'react';
import Button from '../Button';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import Callout from 'src/components/InformationCallout/Callout';
import { GenericDialogue } from '../Dialogue/Dialogue';
import Row from '../Row';
import {
  AnnouncementSaveAction,
  useAnnouncementFormOrchestrator,
  UseAnnouncementFormOrchestratorReturn,
} from './hooks/useAnnouncementFormOrchestrator';
import { AnnouncementFormData } from './hooks/useAnnouncementFormEntityState';
import AnnouncementFormHeading from './components/AnnouncementFormHeading/AnnouncementFormHeading';
import PropertyAndScopeSection from './sections/PropertyAndScopeSection';
import AnnouncementDetailsSection from './sections/AnnouncementDetailsSection';
import './AnnouncementForm.scss';

interface AnnouncementFormProps {
  initialData?: Partial<AnnouncementFormData>;
  onClose: () => void;
  onSaved: () => void;
}

const AnnouncementForm = ({
  initialData,
  onClose,
  onSaved,
}: AnnouncementFormProps) => {
  const {
    propertyOptions,
    announcementFormData,
    uiState,
    savingAction,
    canSubmit,
    emptyScopeWarning,
    noUnitsSelectedWarning,
    canAddAttachments,
    sendConfirmationMessage,
    suiteSummaryNames,
    suiteSummaryOverflowCount,
    buildingSelectAll,
    floorSelectAll,
    unitSelectAll,
    onTitleChange,
    onEditorChange,
    onPropertyChange,
    onPlanChange,
    onBuildingsChange,
    onFloorChange,
    onUnitsChange,
    onSelectAllBuildings,
    onSelectAllFloors,
    onSelectAllUnits,
    onSelectAttachments,
    onDeleteAttachment,
    onSave,
  }: UseAnnouncementFormOrchestratorReturn = useAnnouncementFormOrchestrator({
    initialData,
    onClose,
    onSaved,
  });

  const [pendingAction, setPendingAction] =
    useState<AnnouncementSaveAction | null>(null);

  const handleConfirmSend = async () => {
    if (!pendingAction) {
      return;
    }

    await onSave(pendingAction);

    setPendingAction(null);
  };

  return (
    <>
    <GenericDialogue
      isOpen
      onClose={onClose}
      minHeight="92vh"
      maxWidth="135rem"
      title={(announcementFormData.id ? 'Edit' : 'Add an') + ' Announcement'}
      footer={
        <>
          {!announcementFormData.id ? (
            <Button
              color="semantic"
              size="small"
              label={
                savingAction === 'SaveAndAdd'
                  ? 'Sending…'
                  : 'Send and Add Another'
              }
              onClick={() => setPendingAction('SaveAndAdd')}
              disabled={!canSubmit || savingAction !== null}
            />
          ) : (
            <div />
          )}
          <LoadingButton
            actionText="Send Announcement"
            isSubmitting={savingAction === 'SaveAndClose'}
            disabled={!canSubmit || savingAction !== null}
            onClick={() => setPendingAction('SaveAndClose')}
          />
        </>
      }
    >
      <div className="announcement-form">
        <AnnouncementFormHeading>Announcement Details</AnnouncementFormHeading>
        <Row>
          <PropertyAndScopeSection
            propertyOptions={propertyOptions}
            propertyId={announcementFormData.propertyId}
            emptyScopeWarning={emptyScopeWarning}
            noUnitsSelectedWarning={noUnitsSelectedWarning}
            plan={uiState.plan}
            buildingOptions={uiState.buildingOptions}
            levelsOptions={uiState.levelsOptions}
            unitOptions={uiState.unitOptions}
            selectedBuildings={uiState.selectedBuildings}
            selectedFloorValues={uiState.selectedFloorValues}
            selectedUnitValues={uiState.selectedUnitValues}
            buildingSelectAll={buildingSelectAll}
            floorSelectAll={floorSelectAll}
            unitSelectAll={unitSelectAll}
            buildingRemountKey={uiState.buildingRemountKey}
            floorRemountKey={uiState.floorRemountKey}
            unitRemountKey={uiState.unitRemountKey}
            onPropertyChange={onPropertyChange}
            onPlanChange={onPlanChange}
            onBuildingsChange={onBuildingsChange}
            onFloorChange={onFloorChange}
            onUnitsChange={onUnitsChange}
            onSelectAllBuildings={onSelectAllBuildings}
            onSelectAllFloors={onSelectAllFloors}
            onSelectAllUnits={onSelectAllUnits}
          />
          <AnnouncementDetailsSection
            title={announcementFormData.title}
            description={uiState.description}
            attachments={announcementFormData.attachments}
            canAddAttachments={canAddAttachments}
            onTitleChange={onTitleChange}
            onEditorChange={onEditorChange}
            onSelectAttachments={onSelectAttachments}
            onDeleteAttachment={onDeleteAttachment}
          />
        </Row>
      </div>
    </GenericDialogue>

    {pendingAction && (
      <ConfirmationModal
        variant="confirmation"
        title="Send this announcement?"
        message={sendConfirmationMessage}
        warningMessage="Announcements can't be edited once sent."
        confirmLabel="Send Announcement"
        cancelLabel="Cancel"
        isSubmitting={savingAction !== null}
        onConfirm={() => {
          void handleConfirmSend();
        }}
        onCancel={() => setPendingAction(null)}
      >
        {suiteSummaryNames.length > 0 && (
          <div className="announcement-confirm-suites">
            {suiteSummaryNames.map((name: string, index: number) => (
              <CategoryPill key={`${name}-${index}`} text={name} size="small" />
            ))}
            {suiteSummaryOverflowCount > 0 && (
              <CategoryPill
                text={`+${suiteSummaryOverflowCount} more`}
                size="small"
              />
            )}
          </div>
        )}
        {suiteSummaryNames.length === 0 && noUnitsSelectedWarning && (
          <Callout
            variant="warning"
            size="small"
            title={noUnitsSelectedWarning.title}
            subtitle={noUnitsSelectedWarning.subtitle}
          />
        )}
      </ConfirmationModal>
    )}
    </>
  );
};

export default AnnouncementForm;
